// The Ask panel (docs/v3/ASSISTANT.md 1): a question box, streamed answers
// with the links they used, "Was this helpful?", recent chats (30 days, only
// yours) and Contact support. Read-only: it never changes anything.
// Built from the design system's docked Sheet and Chat parts.
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { pageLink } from "@convenor/shared";
import type { AssistantLinkDto } from "@convenor/shared/v3";
import { Button, Notice } from "../../ui";
import { useAssistantChats, useAssistantStatus, useDeleteChat, useFeedback, useHandover, useOpenChat } from "./queries";
import { askStream } from "./stream";

type Msg = { key: string; role: "user" | "assistant"; text: string; links: AssistantLinkDto[]; tool?: string; messageId?: string; feedback?: boolean; failed?: boolean };

const INTRO_KEY = "convenor.assistant.intro";
const readIntro = () => {
  try {
    return localStorage.getItem(INTRO_KEY) === "seen";
  } catch {
    return false;
  }
};

export function AssistantPanel({ audience, onClose }: { audience: "team" | "expert"; onClose: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const fromPath = `${location.pathname}${location.search}`;
  const helpPage = audience === "expert" ? "portalHelp" : "help";
  const helpBase = pageLink(helpPage);
  const helpContact = pageLink(helpPage, {}, { hash: "contact" });
  const status = useAssistantStatus(audience, fromPath);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [chatId, setChatId] = useState<string | undefined>();
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRecent, setShowRecent] = useState(false);
  const [introSeen, setIntroSeen] = useState(readIntro);
  /** What a screen reader hears: the step while answering, then the whole answer once (not every streamed piece). */
  const [announce, setAnnounce] = useState("");
  const recent = useAssistantChats(showRecent);
  const openChat = useOpenChat();
  const deleteChat = useDeleteChat();
  const feedback = useFeedback();
  const handover = useHandover();
  const box = useRef<HTMLTextAreaElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const abort = useRef<AbortController | null>(null);

  const close = useRef(onClose);
  close.current = onClose;

  // Esc closes the panel, also while an answer is coming in; on close, focus goes back to the Ask button.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") close.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus();
    };
  }, []);

  // The newest message stays in view.
  useEffect(() => {
    const el = body.current;
    if (el && !showRecent) el.scrollTop = el.scrollHeight;
  }, [messages, showRecent]);

  useEffect(() => {
    box.current?.focus();
    return () => abort.current?.abort();
  }, [status.data?.available]);

  const update = (key: string, patch: (m: Msg) => Msg) => setMessages((all) => all.map((m) => (m.key === key ? patch(m) : m)));

  const send = async () => {
    const text = question.trim();
    if (!text || busy) return;
    setQuestion("");
    setBusy(true);
    setAnnounce("Answering…");
    const answerKey = `a${Date.now()}`;
    setMessages((all) => [...all, { key: `q${Date.now()}`, role: "user", text, links: [] }, { key: answerKey, role: "assistant", text: "", links: [] }]);
    abort.current = new AbortController();
    await askStream(
      { chatId, text, fromPath, audience },
      {
        chat: (id) => setChatId(id),
        delta: (t) => update(answerKey, (m) => ({ ...m, text: m.text + t, tool: undefined })),
        tool: (label) => {
          update(answerKey, (m) => ({ ...m, tool: label }));
          setAnnounce(`${label}…`);
        },
        // A replaced answer had figures Convenor could not show: the "no data" text takes its place (GL1).
        done: (e) =>
          update(answerKey, (m) => {
            const text = e.replaced && e.text ? e.text : m.text;
            setAnnounce(text);
            return { ...m, text, links: e.links, messageId: e.messageId, tool: undefined };
          }),
        error: (message) => {
          update(answerKey, (m) => ({ ...m, text: m.text || message, failed: true, tool: undefined }));
          setAnnounce(message);
        },
      },
      abort.current.signal,
    );
    setBusy(false);
    box.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const contactSupport = () => {
    if (!chatId) {
      navigate(helpContact, { state: { from: fromPath } });
      return;
    }
    handover.mutate(chatId, { onSettled: (res) => navigate(res?.contactPath ?? helpContact, { state: { from: fromPath, chatId } }) });
  };

  const newChat = () => {
    abort.current?.abort();
    setMessages([]);
    setChatId(undefined);
    setShowRecent(false);
  };

  const open = (id: string) =>
    openChat.mutate(id, {
      onSuccess: (chat) => {
        setChatId(chat.id);
        setMessages(chat.messages.map((m) => ({ key: m.id, role: m.role, text: m.text, links: m.links, messageId: m.role === "assistant" ? m.id : undefined })));
        setShowRecent(false);
      },
    });

  const dismissIntro = () => {
    setIntroSeen(true);
    try {
      localStorage.setItem(INTRO_KEY, "seen");
    } catch {
      // private window: show it again next time
    }
  };

  const unavailable = status.data && !status.data.available;

  return (
    <aside className="sheet sheet--docked" aria-label="Ask">
      <header className="sheet__head">
        <h2 className="sheet__title">Ask</h2>
        <button type="button" className="sheet__close" onClick={onClose}>
          <span aria-hidden="true">×</span>
          <span className="visually-hidden">Close the assistant</span>
        </button>
      </header>

      <div className="sheet__body" ref={body}>
        {!introSeen ? (
          <Notice>
            Answers come from the help centre and from what you can see in Convenor. Azure AI in the EU reads your question. Chats are kept for 30 days, only for you.{" "}
            <button type="button" className="btn btn--text" onClick={dismissIntro}>
              Got it
            </button>
          </Notice>
        ) : null}

        {showRecent ? (
          <section aria-label="Recent chats">
            <h3>Recent chats</h3>
            {recent.data?.chats.length ? (
              <ul className="rows">
                {recent.data.chats.map((c) => (
                  <li key={c.id} className="rows__row">
                    <span className="rows__main">
                      <button type="button" className="btn btn--text" onClick={() => open(c.id)}>
                        {c.title}
                      </button>
                    </span>
                    <Button variant="small" onClick={() => deleteChat.mutate(c.id)}>
                      Delete<span className="visually-hidden"> {c.title}</span>
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted">No chats in the last 30 days.</p>
            )}
          </section>
        ) : (
          <div className="chat">
            {messages.length ? (
              <ol className="chat__log">
                {messages.map((m) => (
                  <li key={m.key} className={`chat__message${m.role === "user" ? " chat__message--person" : ""}`}>
                    {m.failed ? (
                      <Notice kind="warning">{m.text}</Notice>
                    ) : m.text ? (
                      <p className="chat__bubble">{m.text}</p>
                    ) : m.role === "assistant" && busy ? (
                      <p className="chat__busy">{m.tool ?? "Answering"}…</p>
                    ) : null}
                    {m.links.length ? (
                      <ul className="plain-list">
                        {m.links.map((l) => (
                          <li key={l.path}>
                            <Link to={l.path}>{l.label}</Link>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {m.role === "assistant" && m.messageId ? (
                      m.feedback === undefined ? (
                        <p className="text-muted">
                          Was this helpful?{" "}
                          <button
                            type="button"
                            className="btn btn--text"
                            aria-label="Yes, this helped"
                            onClick={() => feedback.mutate({ messageId: m.messageId!, helpful: true }, { onSuccess: () => update(m.key, (x) => ({ ...x, feedback: true })) })}
                          >
                            Yes
                          </button>{" "}
                          <button
                            type="button"
                            className="btn btn--text"
                            aria-label="No, this did not help"
                            onClick={() => feedback.mutate({ messageId: m.messageId!, helpful: false }, { onSuccess: () => update(m.key, (x) => ({ ...x, feedback: false })) })}
                          >
                            No
                          </button>
                        </p>
                      ) : (
                        <p className="text-muted">Thank you.</p>
                      )
                    ) : null}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="chat__empty">Ask how to do something in Convenor, or about the records you can see.</p>
            )}
          </div>
        )}
        <p className="visually-hidden" aria-live="polite">
          {announce}
        </p>
      </div>

      <footer className="sheet__foot">
        {unavailable ? (
          <Notice title={status.data!.message}>
            <ul className="plain-list">
              <li>
                <Link to={helpBase}>Open the help centre</Link>
              </li>
              <li>
                <Link to={helpContact} state={{ from: fromPath }}>
                  Contact support
                </Link>
              </li>
            </ul>
          </Notice>
        ) : status.data ? (
          <form
            className="chat__composer"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <label htmlFor="assistant-question">Your question</label>
            <textarea id="assistant-question" className="chat__box" ref={box} rows={3} value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={onKeyDown} readOnly={busy} maxLength={2000} />
            <div className="chat__send">
              <Button variant="text" onClick={newChat}>
                New chat
              </Button>
              <Button variant="text" onClick={() => setShowRecent((v) => !v)}>
                {showRecent ? "Back to the chat" : "Recent chats"}
              </Button>
              <Button variant="text" onClick={contactSupport}>
                Contact support
              </Button>
              <Button type="submit" busy={busy} busyLabel="Answering…">
                Ask
              </Button>
            </div>
          </form>
        ) : null}
      </footer>
    </aside>
  );
}
